import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, Descriptions, Tabs } from '@arco-design/web-react';

import sectionTabStyles from '@/components-biz/Maas/maas-node-section-tabs.module.less';
import resourceSpecStyles from '@/components/ResourceSpecCard/index.module.less';
import { type MaasGroupSpec, MaasRunMode } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import type { ModelInstanceFormValues } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelInstanceTypes';

import { GroupSpecCard, GroupSpecSection, WorkerGroupConfirmList } from './group-spec-card';
import { formatMaasRunMode } from './labels';
import { resolveSystemDiskGiFromFormValues } from './masterGroupForm';
import { parseNodesField } from './nodeFields';

/** 节点组只读预览内容 props */
export interface MaasNodeGroupsReadonlyContentProps {
  values: ModelInstanceFormValues;
  hideGroupDeployMode?: boolean;
  showInstanceReplicas?: boolean;
  trainingImageOnly?: boolean;
  masterGroupTitle?: string;
  masterGroupRoleLabel?: string;
  systemDiskFromFormOnly?: boolean;
  /** MI/TC：展示节点级启动命令 */
  showNodeStartupCommand?: boolean;
  /** 详情资源规格：按节点组使用 type="text" Tab 切换（与计算资源 Tab 一致） */
  nodeGroupSectionTabs?: boolean;
}

const TabPane = Tabs.TabPane;

/** 节点组只读预览正文（不含外层 Card / 标题） */
export function MaasNodeGroupsReadonlyContent({
  values,
  hideGroupDeployMode = false,
  showInstanceReplicas = true,
  trainingImageOnly = false,
  masterGroupTitle,
  masterGroupRoleLabel,
  systemDiskFromFormOnly = false,
  showNodeStartupCommand = false,
  nodeGroupSectionTabs = false,
}: MaasNodeGroupsReadonlyContentProps) {
  const { t } = useTranslation();
  const [activeSectionTab, setActiveSectionTab] = useState<string>();
  const runMode = values.runMode;
  const masterSpec = parseNodesField(values.masterNodes)[0];
  const workerSpecs = parseNodesField(values.workerNodes);

  const resolveSystemDiskGi = (groupKey: 'headGroupSpec' | 'workerGroupSpecs[0]' | 'workers', group?: MaasGroupSpec) => {
    if (!systemDiskFromFormOnly) return resolveSystemDiskGiFromFormValues(values, groupKey, group);
    const value = values[groupKey === 'headGroupSpec' ? 'master.systemDiskSize' : 'worker.systemDiskSize'];
    return typeof value === 'number' && value > 0 ? value : undefined;
  };

  const resolvedMasterSystemDiskSize = resolveSystemDiskGi('headGroupSpec', masterSpec);
  const resolvedWorkerSystemDiskSize = resolveSystemDiskGi('workers', workerSpecs[0]);
  const resolvedPrimarySystemDiskSize = resolveSystemDiskGi('workerGroupSpecs[0]', workerSpecs[0]);

  const deployModeLabel = useMemo(() => formatMaasRunMode(runMode, t), [runMode, t]);

  const isPrimarySecondary = runMode === MaasRunMode.PrimarySecondary;
  const isNormal = runMode === MaasRunMode.Normal || runMode === MaasRunMode.Single || runMode === MaasRunMode.Distributed;
  const showMasterSection = isPrimarySecondary;
  const showWorkerSection = isNormal || isPrimarySecondary;

  const sectionTabs = useMemo(() => {
    if (!nodeGroupSectionTabs) return [];
    const tabs: { key: string; title: string; content: ReactNode }[] = [];

    if (showMasterSection) {
      tabs.push({
        key: 'head',
        title: masterGroupTitle ?? t('data-center:maas.master.group'),
        content: (
          <GroupSpecCard
            role="master"
            masterRoleLabel={masterGroupRoleLabel}
            spec={masterSpec as MaasGroupSpec | undefined}
            systemDiskGi={resolvedMasterSystemDiskSize}
            onEdit={() => undefined}
            readonly
            embeddedPreview
            trainingImageOnly={trainingImageOnly}
            showNodeStartupCommand={showNodeStartupCommand}
          />
        ),
      });
    }

    if (showWorkerSection) {
      tabs.push({
        key: 'worker',
        title: t('data-center:maas.worker.group'),
        content: (
          <WorkerGroupConfirmList
            workers={workerSpecs}
            workerSystemDiskGi={resolvedWorkerSystemDiskSize ?? resolvedPrimarySystemDiskSize}
            embeddedPreview
            trainingImageOnly={trainingImageOnly}
            showNodeStartupCommand={showNodeStartupCommand}
          />
        ),
      });
    }

    return tabs;
  }, [
    masterGroupRoleLabel,
    masterGroupTitle,
    masterSpec,
    nodeGroupSectionTabs,
    resolvedMasterSystemDiskSize,
    resolvedPrimarySystemDiskSize,
    resolvedWorkerSystemDiskSize,
    showMasterSection,
    showNodeStartupCommand,
    showWorkerSection,
    t,
    trainingImageOnly,
    workerSpecs,
  ]);

  const deployModeDescriptions = !hideGroupDeployMode ? (
    <Descriptions
      column={2}
      data={[
        ...(showInstanceReplicas
          ? [
              {
                label: t('data-center:maas.instance.count'),
                value: typeof values.replicas === 'number' ? values.replicas : '-',
              },
            ]
          : []),
        {
          label: t('data-center:maas.deploy.mode'),
          value: deployModeLabel,
        },
      ]}
      border
      style={{ marginBottom: 12 }}
    />
  ) : null;

  if (nodeGroupSectionTabs && sectionTabs.length) {
    const defaultTab = sectionTabs[0].key;
    const activeTab = activeSectionTab && sectionTabs.some((tab) => tab.key === activeSectionTab) ? activeSectionTab : defaultTab;

    return (
      <>
        {deployModeDescriptions}
        <Tabs type="text" activeTab={activeTab} onChange={setActiveSectionTab} destroyOnHide className={sectionTabStyles.sectionTabs}>
          {sectionTabs.map((tab) => (
            <TabPane key={tab.key} title={tab.title}>
              {tab.content}
            </TabPane>
          ))}
        </Tabs>
      </>
    );
  }

  return (
    <>
      {deployModeDescriptions}
      {showMasterSection ? (
        <GroupSpecSection title={masterGroupTitle ?? t('data-center:maas.master.group')}>
          <GroupSpecCard
            role="master"
            masterRoleLabel={masterGroupRoleLabel}
            spec={masterSpec as MaasGroupSpec | undefined}
            systemDiskGi={resolvedMasterSystemDiskSize}
            onEdit={() => undefined}
            readonly
            embeddedPreview
            trainingImageOnly={trainingImageOnly}
            showNodeStartupCommand={showNodeStartupCommand}
          />
        </GroupSpecSection>
      ) : null}
      {showWorkerSection ? (
        <GroupSpecSection title={t('data-center:maas.worker.group')} style={{ marginTop: showMasterSection ? 16 : 0 }}>
          <WorkerGroupConfirmList
            workers={workerSpecs}
            workerSystemDiskGi={resolvedWorkerSystemDiskSize ?? resolvedPrimarySystemDiskSize}
            embeddedPreview
            trainingImageOnly={trainingImageOnly}
            showNodeStartupCommand={showNodeStartupCommand}
          />
        </GroupSpecSection>
      ) : null}
    </>
  );
}

/** 节点组只读预览区块 props（确认步 / 详情资源规格复用） */
export interface MaasNodeGroupsReadonlySectionProps extends MaasNodeGroupsReadonlyContentProps {
  /** 外层 Card 标题 */
  title: string;
  /** 详情资源规格：标题与 ResourceSpecCard 一致，不展示 Card header 分隔线 */
  plainSectionTitle?: boolean;
}

/** 节点组只读预览：GroupSpecCard + 内嵌镜像/探活/共享内存/网卡摘要，与创建确认步一致 */
export function MaasNodeGroupsReadonlySection({ title, plainSectionTitle = false, ...contentProps }: MaasNodeGroupsReadonlySectionProps) {
  const body = <MaasNodeGroupsReadonlyContent {...contentProps} />;

  if (plainSectionTitle) {
    return (
      <Card bordered={false} className={resourceSpecStyles.info_card}>
        <div className={resourceSpecStyles.card_title}>{title}</div>
        {body}
      </Card>
    );
  }

  return (
    <Card bordered={false} title={title}>
      {body}
    </Card>
  );
}
